import React, { useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { Divider, Grid } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { get, over, sequence } from "@totalsoft/change-tracking-react";
import { remove } from "ramda";
import AssetWithExchangeRate from "./AssetWithExchangeRate";
import Asset from "./Asset";
import ExtraInformation from "./ExtraInformation";
import SectionTitle from "features/common/components/SectionTitle";
import { emptyArray } from "constants/defaultValues";
import { SUPPLIERS_QUERY } from "features/common/queries";
import { LoadingFakeText } from "@totalsoft/rocket-webapp-components-deprecated";
import { useQueryWithErrorHandling } from "hooks/errorHandling";

function AssetList({ quoteLens, validation, showValuesInEuro }) {
  const { t } = useTranslation();
  const quote = quoteLens |> get;

  const editAssetDisabled = useMemo(() => {
    if ([-4, -5, -6, -7, -8, -9, -10, -11, -12, -13].indexOf(quote.quoteStatusId) > -1) return true;
    return false;
  }, [quote.quoteStatusId]);
  const AssetComponent = showValuesInEuro ? AssetWithExchangeRate : Asset;

  const handleRemoveAsset = useCallback((index) => over(quoteLens.quoteAssets, remove(index, 1)), [
    quoteLens.quoteAssets,
  ]);

  const { data, loading } = useQueryWithErrorHandling(SUPPLIERS_QUERY, { skip: !quote?.quoteAssets?.length });
  const suppliers = data?.suppliers || emptyArray;

  if (loading) {
    return <LoadingFakeText lines={10} />;
  }

  return (
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <SectionTitle title={t("Asset.SelectedAssets")} extraComponent={<Divider />} />
      </Grid>
      {(quoteLens.quoteAssets |> sequence).map((assetLens, index) => (
        <Grid item xs={12} key={assetLens.quoteAssetId |> get}>
          <AssetComponent
            quote={quote}
            index={index}
            assetLens={assetLens}
            suppliers={suppliers}
            onRemoveAsset={handleRemoveAsset}
            editAssetDisabled={editAssetDisabled}
            validation={validation.quoteAssets[index]}
          />
        </Grid>
      ))}
      <ExtraInformation quote={quote} showValuesInEuro={showValuesInEuro || false} />
    </Grid>
  );
}

AssetList.propTypes = {
  quoteLens: PropTypes.object.isRequired,
  validation: PropTypes.object.isRequired,
  showValuesInEuro: PropTypes.bool.isRequired,
};

export default AssetList;
